
import { format } from "date-fns";
import { MembershipFormData } from "../types";

interface ApplicationSubmissionResponse {
  success: boolean;
  applicationId?: string;
  error?: string;
  data?: any;
}

export const prepareApplicationData = (values: MembershipFormData) => {
  const formattedDate = format(values.applicationDate, 'yyyy-MM-dd');
  
  // Convert potato area values to string type to match database column type
  const currentPotatoArea = values.currentPotatoArea ? String(values.currentPotatoArea) : null;
  const totalPotatoArea = values.totalPotatoArea ? String(values.totalPotatoArea) : null;
  const plannedPotatoArea = values.plannedPotatoArea ? String(values.plannedPotatoArea) : null;
  
  // Add source information to help with debugging
  const source = typeof window !== 'undefined' ? window.location.href : 'unknown';
  
  return {
    application_date: formattedDate,
    business_name: values.businessName,
    email: values.email,
    first_name: values.firstName,
    last_name: values.lastName,
    home_address_street1: values.address.street1,
    home_address_street2: values.address.street2 || null,
    home_address_city: values.address.city,
    home_address_postal_code: values.address.postalCode,
    business_address_street1: values.businessAddress.street1,
    business_address_street2: values.businessAddress.street2 || null,
    business_address_city: values.businessAddress.city,
    business_address_postal_code: values.businessAddress.postalCode,
    mobile_area_code: values.mobileNumber.areaCode,
    mobile_number: values.mobileNumber.number,
    business_area_code: values.businessNumber.areaCode,
    business_number: values.businessNumber.number,
    membership_type: values.membershipType,
    current_potato_area: currentPotatoArea,
    total_potato_area: totalPotatoArea,
    planned_potato_area: plannedPotatoArea,
    signature: values.signature || null,
    source: source,
    captcha_token: values.captchaToken // Add captcha_token to the prepared data
  };
};

export const submitApplicationData = async (applicationData: any): Promise<ApplicationSubmissionResponse> => {
  try {
    // Log submission attempt with detailed debugging
    console.log("Attempting to submit application with data:", {
      business_name: applicationData.business_name,
      email: applicationData.email,
      timestamp: new Date().toISOString(),
      client_type: typeof window !== 'undefined' ? 
        `${window.navigator.userAgent} | Width: ${window.innerWidth}px` : 'unknown'
    });
    
    // Instead of using the Supabase client or direct REST API, use the edge function
    // This will bypass RLS policies by using service role keys on the server side
    console.log("Using edge function to bypass RLS");
    
    const endpoint = 'https://cspvympuvqbiohwqvgjy.supabase.co/functions/v1/submit-membership-application';
    console.log(`Sending request to: ${endpoint}`);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ application: applicationData })
    });

    // Detailed response logging
    console.log("API Response Status:", response.status);
    console.log("Response Headers:", Object.fromEntries([...response.headers.entries()]));
    
    const responseData = await response.json();
    console.log("Response Data:", responseData);
    
    if (!response.ok) {
      console.error("API Error Details:", {
        status: response.status,
        statusText: response.statusText,
        body: responseData
      });
      
      return { 
        success: false, 
        error: `API Error: ${response.status} - ${JSON.stringify(responseData)}` 
      };
    }

    console.log("Successful API Response Data:", responseData);
    
    const applicationId = responseData.id;

    if (!applicationId) {
      console.error("Missing application ID in response:", responseData);
      return { 
        success: false, 
        error: "Application submitted but no ID was returned" 
      };
    }

    console.log("Application submitted successfully, ID:", applicationId);
    return { 
      success: true, 
      applicationId,
      data: responseData
    };
  } catch (error: any) {
    console.error("Error submitting application data:", error);
    return { 
      success: false, 
      error: error.message || "Unknown error submitting application" 
    };
  }
};
